var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import './DocumentView.scss';
import { useApolloClient } from '@apollo/client';
import { Document, Font, Page, StyleSheet, usePDF } from '@react-pdf/renderer';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import GET_PRODUCT from 'api/queries/GET_PRODUCT';
import GET_SUPPLIER from 'api/queries/GET_SUPPLIER';
import PrinterSvg from 'images/v5/icons/printer.svg';
import Button from 'layout/atoms/Button/Button';
import Loader from 'layout/atoms/Loader/Loader';
import Grid from 'layout/molecules/Grid/Grid';
import Tools from 'layout/organisms/Tools';
import Dashboard from 'layout/templates/Dashboard/Dashboard';
import PdfPreview from 'organisms/PdfGenerator/Previews/PdfPreview';
import { parseProduct } from 'parsers/specification/parseProduct';
import parseSpecificationDocument from 'parsers/specification/parseSpecificationDocument';
import { clearBreadcrumbs, setBreadcrumbs } from 'store/layoutSlice';
import { getMenuLabel } from 'utils/getMenuItem';
import ComponentPdfPage from './Common/ComponentPdfPage';
import Footer from './Common/Footer';
import Header from './Common/Header';
import DescriptionPage from './Pages/DescriptionPage';
import DetailsPage from './Pages/DetailsPage';
import DimensionsPage from './Pages/DimensionsPage';
import ProductsPage from './Pages/ProductsPage';
import ProjectionsPage from './Pages/ProjectionsPage';
import VariantsPage from './Pages/VariantsPage';
// react-pdf's built-in Helvetica font doesn't support Polish diacritics (ą, ć, ę, ł, ń, ó, ś, ź, ż),
// so we register Inter (already used across the app) and apply it via the page's fontFamily below.
Font.register({
    family: 'Inter',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.ttf',
        },
        {
            src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuFuYMZg.ttf',
            fontWeight: 'bold',
        },
    ],
});
const styles = StyleSheet.create({
    page: {
        fontFamily: 'Inter',
        fontWeight: 'medium',
        paddingTop: '3%',
        paddingBottom: '4%',
    },
});
const MAX_IMAGE_DIMENSION = 800;
function resizeImageToJpeg(url) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!url || typeof url !== 'string' || url.startsWith('data:'))
            return url;
        try {
            const response = yield fetch(url);
            if (!response.ok) {
                return null;
            }
            const blob = yield response.blob();
            const blobUrl = URL.createObjectURL(blob);
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    try {
                        let { width, height } = img;
                        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
                            const ratio = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);
                            width = Math.round(width * ratio);
                            height = Math.round(height * ratio);
                        }
                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        if (!ctx)
                            throw new Error('Could not get canvas context');
                        ctx.drawImage(img, 0, 0, width, height);
                        URL.revokeObjectURL(blobUrl);
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                        resolve(dataUrl);
                    }
                    catch (_a) {
                        URL.revokeObjectURL(blobUrl);
                        resolve(null);
                    }
                };
                img.onerror = () => {
                    URL.revokeObjectURL(blobUrl);
                    resolve(null);
                };
                img.src = blobUrl;
            });
        }
        catch (_a) {
            return null;
        }
    });
}
function preprocessSpecImages(specification) {
    return __awaiter(this, void 0, void 0, function* () {
        const [mainPhoto, processedProjections, processedFiles] = yield Promise.all([
            resizeImageToJpeg(specification.mainPhoto),
            Promise.all((specification.projections || []).map((projection) => __awaiter(this, void 0, void 0, function* () {
                const [picture, processedPoints] = yield Promise.all([
                    resizeImageToJpeg(projection.picture),
                    Promise.all((projection.points || []).map((point) => __awaiter(this, void 0, void 0, function* () {
                        return Object.assign(Object.assign({}, point), { photo: yield resizeImageToJpeg(point.photo) });
                    }))),
                ]);
                return Object.assign(Object.assign({}, projection), { picture, points: processedPoints });
            }))),
            Promise.all((specification.files || []).map((file) => __awaiter(this, void 0, void 0, function* () {
                return (Object.assign(Object.assign({}, file), { file: yield resizeImageToJpeg(file.file) }));
            }))),
        ]);
        return Object.assign(Object.assign({}, specification), { mainPhoto, projections: processedProjections, files: processedFiles });
    });
}
function SpecificationPage({ specification, productLabels, }) {
    const date = new Date();
    return (React.createElement(Page, { size: "A4", style: styles.page },
        React.createElement(Header, { title: specification.code }),
        React.createElement(DetailsPage, { specification: specification }),
        specification.variants.length > 0 && (React.createElement(VariantsPage, { variants: specification.variants, sizes: specification.sizes })),
        specification.elements.length > 0 && (React.createElement(DimensionsPage, { elements: specification.elements, sizes: specification.sizes })),
        React.createElement(DescriptionPage, { description: specification.description, files: specification.files }),
        React.createElement(ProductsPage, { components: specification.components, productLabels: productLabels }),
        specification.projections.length > 0 && (React.createElement(ProjectionsPage, { projections: specification.projections })),
        React.createElement(Footer, Object.assign({ date: date }, specification.footer))));
}
function SpecificationView({ specification, t, productLabels, }) {
    return (React.createElement(Document, { language: "Polish", title: t('specification_capitalized') },
        React.createElement(SpecificationPage, { specification: specification, productLabels: productLabels })));
}
function PdfGenerator({ specId, processedSpec, productLabels, title, includeComponents, componentsPdfData, }) {
    const { t, i18n } = useTranslation(['specification', 'common']);
    const specificationView = useMemo(() => {
        if (includeComponents) {
            return (React.createElement(Document, { language: "Polish", title: t('specification_capitalized') },
                React.createElement(SpecificationPage, { specification: processedSpec, productLabels: productLabels }),
                (componentsPdfData || []).map(({ id, component, supplierDetails }) => (React.createElement(ComponentPdfPage, { key: id, component: component, supplierDetails: supplierDetails, t: t, i18n: i18n })))));
        }
        return React.createElement(SpecificationView, { specification: processedSpec, t: t, productLabels: productLabels });
    }, [componentsPdfData, i18n, includeComponents, processedSpec, productLabels, t]);
    const [{ loading, url, error }] = usePDF({
        document: specificationView,
    });
    const downloadName = includeComponents
        ? `Specyfikacja_z_komponentami__${title}.pdf`
        : `Specyfikacja__${title}.pdf`;
    if (error) {
        return (React.createElement("div", { className: "DocumentError" }, t('common:error_with_message', { errorMessage: error })));
    }
    if (loading || !url) {
        return (React.createElement(Dashboard, null,
            React.createElement(Loader, null)));
    }
    return (React.createElement(Dashboard, { headerAside: React.createElement("a", { href: url, className: "download", download: downloadName },
            React.createElement(Button, { color: "primary", leftIcon: React.createElement(PrinterSvg, null) }, t('common:download'))) },
        React.createElement(Grid, { vp: 12, hp: 24 },
            React.createElement(PdfPreview, { file: url, name: downloadName.replace(/\.pdf$/, ''), isExtended: true })),
        React.createElement(Tools, { url: `/specifications/details/${specId}` })));
}
function DocumentView({ data, productLabels, includeComponents }) {
    var _a, _b;
    const { t, i18n } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const apolloClient = useApolloClient();
    const specification = useMemo(() => parseSpecificationDocument(data), [data]);
    const [processedSpec, setProcessedSpec] = useState(null);
    const [componentsPdfData, setComponentsPdfData] = useState(null);
    useEffect(() => {
        var _a, _b, _c;
        const specificationsLabel = getMenuLabel('/specifications', t);
        dispatch(setBreadcrumbs([
            { to: '/specifications', name: specificationsLabel },
            {
                to: `/specifications/details/${(_a = data === null || data === void 0 ? void 0 : data.node) === null || _a === void 0 ? void 0 : _a.id}/summary`,
                name: specification.title || '-',
            },
            {
                to: includeComponents
                    ? `/specifications/pdf-components/${(_b = data === null || data === void 0 ? void 0 : data.node) === null || _b === void 0 ? void 0 : _b.id}`
                    : `/specifications/pdf/${(_c = data === null || data === void 0 ? void 0 : data.node) === null || _c === void 0 ? void 0 : _c.id}`,
                name: includeComponents
                    ? t('pdf_components_file', { title: specification.title })
                    : t('pdf_file', { title: specification.title }),
            },
        ]));
        return () => {
            dispatch(clearBreadcrumbs());
        };
    }, [dispatch, t, (_a = data === null || data === void 0 ? void 0 : data.node) === null || _a === void 0 ? void 0 : _a.id, specification.title, includeComponents]);
    useEffect(() => {
        let cancelled = false;
        preprocessSpecImages(specification).then((processed) => {
            if (!cancelled)
                setProcessedSpec(processed);
        });
        return () => {
            cancelled = true;
        };
    }, [specification]);
    const componentProductIds = useMemo(() => {
        if (!includeComponents)
            return [];
        const ids = specification.components
            .map((component) => { var _a, _b; return (_b = (_a = component === null || component === void 0 ? void 0 : component.productSnapshot) === null || _a === void 0 ? void 0 : _a.product) === null || _b === void 0 ? void 0 : _b.id; })
            .filter(Boolean);
        return Array.from(new Set(ids));
    }, [includeComponents, specification.components]);
    useEffect(() => {
        if (!includeComponents) {
            setComponentsPdfData(null);
            return undefined;
        }
        if (componentProductIds.length === 0) {
            setComponentsPdfData([]);
            return undefined;
        }
        let cancelled = false;
        Promise.all(componentProductIds.map((productId) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const { data: productData } = yield apolloClient.query({
                query: GET_PRODUCT,
                variables: { productId },
            });
            const supplierId = (_b = (_a = productData === null || productData === void 0 ? void 0 : productData.node) === null || _a === void 0 ? void 0 : _a.supplier) === null || _b === void 0 ? void 0 : _b.id;
            let supplierDetails;
            if (supplierId) {
                const { data: supplierData } = yield apolloClient.query({
                    query: GET_SUPPLIER,
                    variables: { id: supplierId },
                });
                supplierDetails = supplierData === null || supplierData === void 0 ? void 0 : supplierData.node;
            }
            const component = parseProduct(productData === null || productData === void 0 ? void 0 : productData.node, i18n, (_c = productLabels === null || productLabels === void 0 ? void 0 : productLabels.autocomplete) === null || _c === void 0 ? void 0 : _c.productLabels);
            return { id: productId, component, supplierDetails };
        }))).then((results) => {
            if (!cancelled)
                setComponentsPdfData(results);
        });
        return () => {
            cancelled = true;
        };
    }, [apolloClient, componentProductIds, i18n, includeComponents, productLabels]);
    useEffect(() => {
        const onError = (event) => {
            // eslint-disable-next-line no-console
            console.error('[PDF] window.onerror:', event.message, '| source:', event.filename, '| line:', event.lineno, '| col:', event.colno, '| error:', event.error);
        };
        const onUnhandledRejection = (event) => {
            // eslint-disable-next-line no-console
            console.error('[PDF] Unhandled promise rejection:', event.reason);
        };
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onUnhandledRejection);
        return () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
        };
    }, []);
    if (!processedSpec || (includeComponents && componentsPdfData === null)) {
        return (React.createElement(Dashboard, null,
            React.createElement(Loader, null)));
    }
    return (React.createElement(PdfGenerator, { specId: (_b = data === null || data === void 0 ? void 0 : data.node) === null || _b === void 0 ? void 0 : _b.id, processedSpec: processedSpec, productLabels: productLabels, title: specification.title, includeComponents: includeComponents, componentsPdfData: componentsPdfData }));
}
export default memo(DocumentView);
